import { BikeConfig, DEFAULT_BIKE } from '../config';
import { vec } from '../math';

/**
 * One bike of the school fleet: the published numbers (spec sheet), what the garage shows, and how they map onto
 * the physics BikeConfig. Physics code never reads BikeSpec; it gets `bikeConfig(spec)`.
 *
 * Mapping to BikeConfig (the physics was tuned on DEFAULT_BIKE, 180 kg bike + rider, so everything that scales
 * with mass is scaled from it rather than invented):
 * - mass = dry mass + fuel (about 6 kg) + RIDER_MASS; inertia, spring and damping scale with mass / 180
 *   (same static sag and damping ratio as the tuned default);
 * - engineTorque = published peak crank torque, torqueCurve = its shape (fraction of peak by rpm);
 * - gearRatios = primary * gearbox * final drive (engine rpm per wheel rpm), one per gear;
 * - suspensionTravel = mean of fork and shock travel, wheelbase = published wheelbase.
 */
export type Stroke = '2T' | '4T';
export type RiderLevel = 'beginner' | 'intermediate' | 'advanced';
/** Where the school lets a bike ride: 'track' = the Zielonka training ring and main track (drills too), 'enduro' = the enduro route. */
export type Discipline = 'track' | 'enduro';

export interface BikeSpec {
  id: string;
  brand: 'Yamaha' | 'Honda' | 'KTM' | 'GASGAS';
  name: string;            // model name as sold
  year: number;            // model year the numbers are from
  kind: 'mx' | 'enduro';
  stroke: Stroke;
  displacement: number;    // cc
  dryMass: number;         // kg, without fuel
  peakPower: number;       // kW
  peakPowerRpm: number;
  peakTorque: number;      // N*m at the crank
  peakTorqueRpm: number;
  limiterRpm: number;
  idleRpm: number;
  torqueCurve: readonly (readonly [number, number])[]; // [rpm, fraction of peak torque]
  primary: number;         // primary reduction
  gearbox: readonly number[];
  finalDrive: number;      // rear sprocket / front sprocket
  travelFront: number;     // mm
  travelRear: number;      // mm
  seatHeight: number;      // mm
  wheelbase: number;       // mm
  level: RiderLevel;       // who the school gives it to
  discipline: Discipline;  // the one kind of level the school rides it on
  livery: string;          // src/ui/logic/livery.ts BIKES id
  /**
   * Rideable model: `file` is its public/assets path (scripts/fleet/build.mjs; none = the KTM bike.glb), `base` what
   * the model really is, `approximate` when that is another make, model year or displacement.
   */
  model: { file?: string; base: string; approximate: boolean };
  audio: { set: '2t' | '4t'; pitch: number }; // sample set and pitch trim of the voice
  /**
   * Engine map the school runs: fraction of the torque below the torque peak (the enduro bikes have a map switch;
   * the soft map tames the bottom so a full-throttle launch in 1st does not loop the bike out, the top end stays).
   * 1 = full map.
   */
  schoolMap?: number;
  /** Fields that are estimates (no manufacturer figure found), so the garage and docs can say so. */
  estimated: readonly (keyof BikeSpec)[];
}

export const RIDER_MASS = 75;
export const FUEL_MASS = 6;
const BASE_MASS = DEFAULT_BIKE.mass;

export const overallRatios = (s: BikeSpec): number[] => s.gearbox.map((g) => +(s.primary * g * s.finalDrive).toFixed(2));

/** The physics config of a fleet bike: DEFAULT_BIKE with the spec's overrides. */
export function bikeConfig(s: BikeSpec): BikeConfig {
  const d = DEFAULT_BIKE;
  const mass = s.dryMass + FUEL_MASS + RIDER_MASS;
  const k = mass / BASE_MASS;
  const span = s.limiterRpm - s.idleRpm;
  return {
    ...d,
    mass,
    inertia: vec(d.inertia.x * k, d.inertia.y * k, d.inertia.z * k),
    wheelbase: s.wheelbase / 1000,
    suspensionTravel: (s.travelFront + s.travelRear) / 2000,
    forkTravel: s.travelFront / 1000,
    shockTravel: s.travelRear / 1000,
    springRate: d.springRate * k,
    springProgression: d.springProgression * k,
    damping: d.damping * k,
    riderMass: RIDER_MASS,
    reboundDamping: d.reboundDamping * k,
    engineTorque: s.peakTorque,
    torqueCurve: schoolMapCurve(s),
    gearRatios: overallRatios(s),
    idleRpm: s.idleRpm,
    // A two-stroke is launched on the pipe; a four-stroke pulls from lower down.
    launchRpm: Math.round(s.idleRpm + span * (s.stroke === '2T' ? 0.62 : 0.5)),
    shiftUpRpm: Math.round(s.limiterRpm * 0.93),
    shiftDownRpm: Math.round(s.idleRpm + span * 0.36),
    limiterRpm: s.limiterRpm,
    // Four-strokes hold back on a shut throttle much harder than two-strokes.
    engineBrake: s.stroke === '4T' ? d.engineBrake * (s.displacement / 250) : d.engineBrake * 0.45,
  };
}

/** The spec's torque curve with the school map applied (points below the torque peak scaled by schoolMap). */
export function schoolMapCurve(s: BikeSpec): readonly (readonly [number, number])[] {
  const m = s.schoolMap ?? 1;
  return s.torqueCurve.map(([rpm, f]) => [rpm, rpm < s.peakTorqueRpm ? +(f * m).toFixed(3) : f] as const);
}
